import { useState } from 'react';
import AdminModal from '../components/AdminModal';
import ConfirmDialog from '../components/ConfirmDialog';
import AdminPagination from '../components/AdminPagination';
import AdminSteps from '../components/AdminSteps';
import { usePagination } from '../hooks/usePagination';
import { useAdminPersist } from '../AdminPersistContext';
import { useEditorFeedback } from '../hooks/useEditorFeedback';
import { useNotifyFormDirty } from '../hooks/useNotifyFormDirty';

const PAGE_SIZE = 10;

const emptyMember = () => ({
  name: 'New Member',
  role: 'Role',
  image: '',
  avatarPosition: 'center top',
  featured: false,
  spotlight: false,
});

export default function TeamEditor({ content, onChange }) {
  const team = content.team ?? [];
  const [editIndex, setEditIndex] = useState(null);
  const [isNewMember, setIsNewMember] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const { persistNow } = useAdminPersist();
  const feedback = useEditorFeedback({ persistNow });

  const { page, setPage, totalPages, paginatedItems, totalItems } = usePagination(
    team.map((member, index) => ({ member, index })),
    PAGE_SIZE
  );

  const update = (next) => onChange({ ...content, team: next });

  const editing = editIndex !== null ? team[editIndex] : null;

  const openAdd = () => {
    const next = [...team, emptyMember()];
    update(next);
    setEditIndex(next.length - 1);
    setIsNewMember(true);
    setPage(Math.ceil(next.length / PAGE_SIZE));
  };

  const saveMember = (form) => {
    update(team.map((m, i) => (i === editIndex ? { ...m, ...form } : m)));
    setEditIndex(null);
    setIsNewMember(false);
    feedback.notifySaved('Team member saved to the server.');
  };

  const discardModal = () => {
    if (isNewMember && editIndex !== null) {
      update(team.filter((_, i) => i !== editIndex));
    }
    setEditIndex(null);
    setIsNewMember(false);
    feedback.setFormDirty(false);
  };

  const closeModal = () => {
    feedback.requestClose({ isNew: isNewMember, onDiscard: discardModal });
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel__head">
        <div>
          <h2>Team photos</h2>
          <p className="admin-panel__desc">Team photos on the About page.</p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openAdd}>
          Add member
        </button>
      </div>

      <AdminSteps />

      <div className="admin-toolbar">
        <span className="admin-toolbar__meta">{totalItems} member(s)</span>
      </div>

      <div className="admin-list">
        {paginatedItems.map(({ member, index }) => (
          <div key={`${member.name}-${index}`} className="admin-list-item">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {member.image ? (
                <img src={member.image} alt="" className="admin-preview-img" />
              ) : (
                <div
                  className="admin-preview-img"
                  style={{ background: '#e8ecf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#888' }}
                >
                  No img
                </div>
              )}
              <div>
                <strong>{member.name}</strong>
                <br />
                <span>{member.role}</span>
                {member.featured ? ' · Featured' : ''}
                {member.spotlight ? ' · Spotlight' : ''}
              </div>
            </div>
            <div className="admin-list-actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => setEditIndex(index)}
              >
                Edit
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger admin-btn--sm"
                onClick={() => setDeleteIndex(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <AdminPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      <AdminModal
        open={editIndex !== null}
        onClose={closeModal}
        title={isNewMember ? 'Add team member' : `Edit: ${editing?.name || ''}`}
        size="md"
      >
        {editing !== null && editIndex !== null ? (
          <TeamMemberForm
            member={editing}
            onSave={saveMember}
            onCancel={closeModal}
            onDirtyChange={feedback.setFormDirty}
          />
        ) : null}
      </AdminModal>

      <ConfirmDialog
        open={deleteIndex !== null}
        title="Remove team member"
        message={`Remove ${team[deleteIndex]?.name || 'this member'}? Click Publish changes after removing to update the live site.`}
        confirmLabel="Remove"
        danger
        onConfirm={() => {
          update(team.filter((_, i) => i !== deleteIndex));
          if (editIndex === deleteIndex) setEditIndex(null);
          setDeleteIndex(null);
          feedback.notifySaved('Team member removed. Saving to the server…');
        }}
        cancelLabel="Cancel"
        onCancel={() => setDeleteIndex(null)}
      />

      {feedback.dialogs}
    </div>
  );
}

function TeamMemberForm({ member, onSave, onCancel, onDirtyChange }) {
  const [form, setForm] = useState({ ...member });
  useNotifyFormDirty(form, member, onDirtyChange);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      {form.image ? (
        <img src={form.image} alt="" className="admin-preview-img" style={{ marginBottom: 16 }} />
      ) : null}
      <div className="admin-field">
        <label>Name</label>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </div>
      <div className="admin-field">
        <label>Role</label>
        <input value={form.role} onChange={(e) => set('role', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Image URL</label>
        <input
          value={form.image || ''}
          onChange={(e) => set('image', e.target.value)}
          placeholder="https://res.cloudinary.com/..."
        />
      </div>
      <label className="admin-checkbox">
        <input
          type="checkbox"
          checked={!!form.featured}
          onChange={(e) => set('featured', e.target.checked)}
        />
        Featured on grid
      </label>
      <label className="admin-checkbox">
        <input
          type="checkbox"
          checked={!!form.spotlight}
          onChange={(e) => set('spotlight', e.target.checked)}
        />
        Spotlight card
      </label>
      <div className="admin-form-actions">
        <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="admin-btn admin-btn--primary">
          Save member
        </button>
      </div>
    </form>
  );
}
